
import { defineComponent } from 'vue';
import { mdiClose } from '@mdi/js';
import { getModule } from 'vuex-module-decorators';
import moment from 'moment';
import Icon from '../../Icon.vue';
import SubscriptionState from '../../../store/modules/subscriptions';
import UserState from '../../../store/modules/users';
import StatState, { IStatQueryModel } from '../../../store/modules/stats';
import {
    CancelScreen,
    CancelPath,
    CancelIntent,
    CancelUsageStats,
    CANCEL_REASONS,
    OFFERS,
    resolvePath,
    getForcedPath,
    isCancelDevMode,
    nextScreen,
    loadUsageStats,
    statsAreAvailable,
    formatPeriodEnd,
    describeDiscount,
    IDiscountedSubscription,
} from './cancelFlow';

import CancelIntercept from './steps/CancelIntercept.vue';
import CancelConfirm from './steps/CancelConfirm.vue';
import CancelFeedback from './steps/CancelFeedback.vue';
import CancelOfferAnnual from './steps/CancelOfferAnnual.vue';
import CancelOfferAnnualEscalated from './steps/CancelOfferAnnualEscalated.vue';
import CancelOfferMonthly from './steps/CancelOfferMonthly.vue';
import CancelFinalize from './steps/CancelFinalize.vue';
import CancelAcceptedAnnual from './steps/CancelAcceptedAnnual.vue';
import CancelAcceptedMonthly from './steps/CancelAcceptedMonthly.vue';

// Per-screen illustrations (580x800, exported from Figma, include the mint background).
import imgIntercept from '@/assets/img/cancel-flow/intercept.png';
import imgConfirm from '@/assets/img/cancel-flow/confirm.png';
import imgFeedback from '@/assets/img/cancel-flow/feedback.png';
import imgOfferAnnual from '@/assets/img/cancel-flow/offer-annual.png';
import imgOfferEscalated from '@/assets/img/cancel-flow/offer-escalated.png';
import imgOfferMonthly from '@/assets/img/cancel-flow/offer-monthly.png';
import imgFinalize from '@/assets/img/cancel-flow/finalize.png';
import imgAccepted from '@/assets/img/cancel-flow/accepted.png';

const SCREEN_ILLUSTRATION: Record<CancelScreen, string> = {
    [CancelScreen.Intercept]: imgIntercept,
    [CancelScreen.Confirm]: imgConfirm,
    [CancelScreen.Feedback]: imgFeedback,
    [CancelScreen.OfferAnnual]: imgOfferAnnual,
    [CancelScreen.OfferAnnualEscalated]: imgOfferEscalated,
    [CancelScreen.OfferMonthly]: imgOfferMonthly,
    [CancelScreen.Finalize]: imgFinalize,
    [CancelScreen.AcceptedAnnual]: imgAccepted,
    [CancelScreen.AcceptedMonthly]: imgAccepted,
};

// Map each screen enum to its component name.
const SCREEN_COMPONENTS: Record<CancelScreen, string> = {
    [CancelScreen.Intercept]: 'CancelIntercept',
    [CancelScreen.Confirm]: 'CancelConfirm',
    [CancelScreen.Feedback]: 'CancelFeedback',
    [CancelScreen.OfferAnnual]: 'CancelOfferAnnual',
    [CancelScreen.OfferAnnualEscalated]: 'CancelOfferAnnualEscalated',
    [CancelScreen.OfferMonthly]: 'CancelOfferMonthly',
    [CancelScreen.Finalize]: 'CancelFinalize',
    [CancelScreen.AcceptedAnnual]: 'CancelAcceptedAnnual',
    [CancelScreen.AcceptedMonthly]: 'CancelAcceptedMonthly',
};

export default defineComponent({
    name: 'CancelFlowModal',

    components: {
        Icon,
        CancelIntercept, CancelConfirm, CancelFeedback,
        CancelOfferAnnual, CancelOfferAnnualEscalated, CancelOfferMonthly,
        CancelFinalize, CancelAcceptedAnnual, CancelAcceptedMonthly,
    },

    props: {
        modelValue: { type: Boolean, default: false },
        isPO: { type: Boolean, default: false },
        billingInterval: { type: String, default: 'year' },
        // The Stripe subscription object (for current_period_end). May be null.
        subscription: { type: Object, default: null },
    },

    emits: ['update:modelValue', 'cancelled'],

    data() {
        return {
            mdiClose,
            reasons: CANCEL_REASONS,
            currentScreen: CancelScreen.Intercept as CancelScreen,
            path: 'annual' as CancelPath,
            history: [] as CancelScreen[],
            stats: null as CancelUsageStats | null,
            ready: false,
            selectedReasons: [] as string[],
            otherText: '',
            isFinalizing: false,
            cancelError: '',
            // FLOCK-6715: feedback validation message. Kept apart from
            // cancelError so a Stripe failure and a missing reason cannot
            // clear each other.
            feedbackError: '',
            isClaiming: false,
            acceptedOfferLabel: '',
            appliedDiscountLabel: '',
            // Offers the user declined this run; reported on the cancelled event.
            offersDeclined: 0,
            // True if the account already used its one-time retention offer — skips
            // the offer screens (set in openFlow from the real subscription/org data).
            offerAlreadyUsed: false,
        };
    },

    computed: {
        visible: {
            get(): boolean {
                return this.modelValue;
            },
            set(val: boolean) {
                this.$emit('update:modelValue', val);
            },
        },

        activeComponent(): string {
            return SCREEN_COMPONENTS[this.currentScreen];
        },

        activeIllustration(): string {
            return SCREEN_ILLUSTRATION[this.currentScreen];
        },

        periodEndLabel(): string {
            const sub = this.subscription as { current_period_end?: number } | null;
            return formatPeriodEnd(sub ? sub.current_period_end : null);
        },

        // Derived from stats so it is false while stats are null (async load in flight),
        // which keeps Screen 0 on the safe fallback branch until data arrives.
        statsAvailable(): boolean {
            return statsAreAvailable(this.stats);
        },
    },

    watch: {
        modelValue(open: boolean) {
            if (open) this.openFlow();
        },
    },

    methods: {
        async openFlow() {
            this.path = getForcedPath() || resolvePath({ billingInterval: this.billingInterval, isPO: this.isPO });
            this.currentScreen = CancelScreen.Intercept;
            this.history = [];
            this.selectedReasons = [];
            this.otherText = '';
            this.isFinalizing = false;
            this.cancelError = '';
            this.feedbackError = '';
            this.isClaiming = false;
            this.appliedDiscountLabel = '';
            this.stats = null;
            this.ready = false;
            this.offerAlreadyUsed = this.readOfferAlreadyUsed();
            this.offersDeclined = 0;

            // Dev toggles use mock fixtures; real opens fetch trailing-12-month usage stats.
            this.stats = isCancelDevMode()
                ? await loadUsageStats('', this.path)
                : await this.fetchRealStats();

            // Screen 0 exists to show usage. With none to show, skip it and open on
            // Confirm rather than a generic version (Nisreen, 2026-09-09).
            const hasStats = statsAreAvailable(this.stats);
            this.currentScreen = hasStats ? CancelScreen.Intercept : CancelScreen.Confirm;
            this.ready = true;

            // FLOCK-6292: ditto_cancel_modal_open_date every time the modal opens;
            // FLOCK-6692: the cancellation-started event with path and stats state.
            getModule(SubscriptionState).sendCancellationEvent({ type: 'open', path: this.path, statsAvailable: hasStats });
        },

        // True if the org already consumed its one-time retention offer (skips offers).
        // Backend also enforces this, so the offer can never be applied twice.
        readOfferAlreadyUsed(): boolean {
            const billing = getModule(UserState).me?.organization?.billing;
            return !!(billing && billing.cancellation_offer_used);
        },

        // FLOCK-6296: trailing-12-month usage from the stats query. Connections +
        // mirroring hours are available; signage count source is still pending, so
        // it is left null (its Screen 0 row hides until a source is confirmed).
        async fetchRealStats(): Promise<CancelUsageStats> {
            const empty: CancelUsageStats = { connectionCount: null, mirroringHours: null, signageCount: null };
            const orgId = getModule(UserState).me?.organization?.id || '';
            if (!orgId) return empty;

            const statState = getModule(StatState);
            const rangeSelection = {
                dateRange: {
                    rangeStart: moment.utc().subtract(12, 'months').toDate(),
                    rangeEnd: moment.utc().toDate(),
                },
                groupBy: 'month',
            };
            const query = (stat: string): IStatQueryModel => ({
                stats: [stat],
                aggregate: 'sum',
                group: { type: 'organization', _id: orgId },
                organizationId: orgId,
                rangeSelection,
            });
            const sumSeries = (resp: { success: boolean; data?: any[] } | null): number | null => {
                if (!resp || !resp.success || !Array.isArray(resp.data)) return null;
                let total = 0;
                for (const series of resp.data) {
                    const points = (series && series.response) || [];
                    for (const point of points) {
                        const v = Array.isArray(point) ? point[0] : null;
                        if (v != null) total += v;
                    }
                }
                return total;
            };

            try {
                const subscriptionState = getModule(SubscriptionState);
                const [conn, mirror, signage] = await Promise.all([
                    statState.fetchStats(query('connectionCount')),
                    statState.fetchStats(query('timeMirroring')),
                    subscriptionState.fetchCancellationStats(),
                ]);
                const mirroringSeconds = sumSeries(mirror);
                // FLOCK-6296: signage items in the org's library. Zero or unknown
                // hides the row; it never renders as "0 signage items".
                const signageItems = signage.success && signage.data ? signage.data.signageItems : null;
                return {
                    connectionCount: sumSeries(conn),
                    mirroringHours: mirroringSeconds != null ? Math.round(mirroringSeconds / 3600) : null,
                    signageCount: signageItems != null && signageItems > 0 ? signageItems : null,
                };
            } catch {
                return empty;
            }
        },

        // FLOCK-6715: the Feedback screen must carry a reason before the
        // cancellation runs; "Other" additionally requires its free-text.
        // Returns the message to show, or '' when the screen is complete.
        feedbackValidationError(): string {
            if (this.selectedReasons.length === 0) {
                return 'Select at least one reason to continue.';
            }
            if (this.selectedReasons.indexOf('other') >= 0 && !this.otherText.trim()) {
                return 'Tell us a little about why you are leaving to continue.';
            }
            return '';
        },

        onOtherText(value: string) {
            this.otherText = value;
            this.feedbackError = '';
        },

        async goNext(intent?: CancelIntent) {
            const next = nextScreen(this.currentScreen, this.path, intent, this.offerAlreadyUsed);
            if (next === this.currentScreen) return;

            if (intent === 'decline') this.offersDeclined += 1;

            // FLOCK-6692: an offer screen is about to show.
            const offerForScreen: { [key: string]: string } = {
                [CancelScreen.OfferAnnual]: 'annual',
                [CancelScreen.OfferAnnualEscalated]: 'annualEscalated',
                [CancelScreen.OfferMonthly]: 'monthly',
            };
            if (offerForScreen[next]) {
                getModule(SubscriptionState).sendCancellationEvent({ type: 'offer_presented', offer: offerForScreen[next] });
            }

            // Reaching Finalize means the user confirmed cancellation from Feedback.
            // The Stripe cancel must succeed BEFORE Finalize ("we're sorry to see you
            // go") is shown; on failure the user stays on Feedback with the reason.
            if (next === CancelScreen.Finalize) {
                // FLOCK-6715: a cancellation reason is required. Validated here,
                // before the HubSpot event and before the Stripe cancel, so an
                // incomplete screen changes nothing on either side.
                const invalid = this.feedbackValidationError();
                if (invalid) {
                    this.feedbackError = invalid;
                    return;
                }
                this.feedbackError = '';
                if (this.isFinalizing) return;
                this.isFinalizing = true;
                this.cancelError = '';
                // FLOCK-6292: fire ditto_cancel_modal_reason (+ detail if "Other").
                // Fire-and-forget: HubSpot must never block the cancellation.
                getModule(SubscriptionState).sendCancellationEvent({
                    type: 'reason',
                    reasons: [...this.selectedReasons],
                    detail: this.selectedReasons.indexOf('other') >= 0 ? this.otherText : undefined,
                });
                const cancelled = await this.finalizeCancellation();
                this.isFinalizing = false;
                if (!cancelled) return;
            }

            this.history.push(this.currentScreen);
            this.currentScreen = next;
        },

        goBack() {
            const prev = this.history.pop();
            if (prev) this.currentScreen = prev;
        },

        // Cancels the Stripe subscription (cancel_at_period_end). Returns true on
        // success; on failure sets cancelError for the Feedback screen.
        async finalizeCancellation(): Promise<boolean> {
            const sub = this.subscription as { id?: string } | null;
            if (!sub || !sub.id) {
                this.cancelError = 'No active subscription was found to cancel.';
                return false;
            }
            const result = await getModule(SubscriptionState).cancelSubscription({
                subscriptionId: sub.id,
                offersDeclined: this.offersDeclined,
            });
            if (!result.success) {
                this.cancelError = result.reason
                    ? `We could not cancel your subscription: ${result.reason}`
                    : 'We could not cancel your subscription. Please try again.';
                return false;
            }
            this.$emit('cancelled');
            return true;
        },

        async onClaim() {
            if (this.isClaiming) return; // guard against double-apply
            // Pick the offer for the current screen: 3a (annual), 3b (escalated), 3c (monthly).
            const offer = this.path === 'monthly'
                ? OFFERS.monthly
                : this.currentScreen === CancelScreen.OfferAnnualEscalated
                    ? OFFERS.annualEscalated
                    : OFFERS.annual;
            const sub = this.subscription as { id?: string } | null;
            this.acceptedOfferLabel = offer.type === 'annualEscalated'
                ? '50% off your next year'
                : offer.type === 'annual'
                    ? '3 months free'
                    : 'one free month';

            // FLOCK-6281: apply the one-time coupon before showing the accepted screen.
            if (!sub || !sub.id) {
                (this as any).$message?.error('No active subscription was found for this offer.');
                return;
            }
            this.isClaiming = true;
            const result = await getModule(SubscriptionState).applyCancellationCoupon({
                subscriptionId: sub.id,
                offer: offer.type,
            });
            this.isClaiming = false;
            if (!result.success) {
                (this as any).$message?.error(
                    result.reason ? `Could not apply the offer: ${result.reason}` : 'Could not apply the offer.',
                );
                return; // stay on the offer screen so the user can retry or cancel
            }
            this.offerAlreadyUsed = true;
            // Screens A/B show what Stripe actually applied, not assumed values.
            // ISubscription does not model Stripe's discount object; read it structurally.
            this.appliedDiscountLabel = describeDiscount(result.data as unknown as IDiscountedSubscription | undefined);

            this.history.push(this.currentScreen);
            this.currentScreen = this.path === 'monthly'
                ? CancelScreen.AcceptedMonthly
                : CancelScreen.AcceptedAnnual;
        },

        onDone() {
            this.visible = false;
        },

        onExit() {
            // "I'll stay" / close-X — leave without cancelling.
            this.visible = false;
        },

        onToggleReason(id: string) {
            const idx = this.selectedReasons.indexOf(id);
            if (idx >= 0) this.selectedReasons.splice(idx, 1);
            else this.selectedReasons.push(id);
            this.feedbackError = '';
        },

        onClosed() {
            // Reset after the dialog finishes closing so state is clean next open.
            this.currentScreen = CancelScreen.Intercept;
            this.history = [];
            this.selectedReasons = [];
            this.otherText = '';
            this.isFinalizing = false;
            this.cancelError = '';
            this.feedbackError = '';
            this.appliedDiscountLabel = '';
            this.ready = false;
        },
    },
});
